// Configuration Management with Protobuf Encoding/Decoding

import * as pako from 'pako';
import { Config, CatalogConfig } from './types';
import { debugConsole } from './debug';
import { librario } from './proto/config';
import { CatalogIDStrings, CatalogIdEnum } from './constants';

/**
 * Map numeric sort value (from dropdown) to protobuf sort order string
 */
export function mapNumericSortToString(numericValue: string): string {
  switch (numericValue) {
    case '0': return 'lastWatched';
    case '1': return 'name';
    case '2': return 'recentlyAdded';
    case '3': return 'oldestAdded';
    case '4': return 'nameDesc';
    case '5': return 'watched';
    case '6': return 'notWatched';
    case '7': return 'timesWatched';
    case '8': return 'lastModified';
    default: return 'lastWatched'; // Default to Last Watched
  }
}

/**
 * Map protobuf sort order string to numeric value (for dropdown)
 */
export function mapStringSortToNumeric(sortOrder: string): string {
  switch (sortOrder) {
    case 'lastWatched': return '0';
    case 'name': return '1';
    case 'recentlyAdded': return '2';
    case 'oldestAdded': return '3';
    case 'nameDesc': return '4';
    case 'watched': return '5';
    case 'notWatched': return '6';
    case 'timesWatched': return '7';
    case 'lastModified': return '8';
    default: return '0'; // Default to Last Watched
  }
}

/**
 * Create default configuration
 */
export function createDefaultConfig(): Config {
  return {
    authKey: '',
    groupByType: true,
    catalogs: [
      {
        id: CatalogIDStrings[CatalogIdEnum.CONTINUE_WATCHING],
        name: 'Continue Watching',
        type: 'mixed',
        sortOrder: 'lastWatched',
        enabled: true,
        position: 0,
        showRemovedItems: false,
        isContinueWatching: true,
      },
      // Default to grouped catalogs when groupByType is true
      {
        id: CatalogIDStrings[CatalogIdEnum.LIBRARY_MOVIE],
        name: 'Library',
        type: 'movie',
        sortOrder: 'lastWatched',
        enabled: true,
        position: 1,
        showRemovedItems: false,
        isContinueWatching: false,
      },
      {
        id: CatalogIDStrings[CatalogIdEnum.LIBRARY_SERIES],
        name: 'Library',
        type: 'series',
        sortOrder: 'lastWatched',
        enabled: true,
        position: 2,
        showRemovedItems: false,
        isContinueWatching: false,
      },
      {
        id: CatalogIDStrings[CatalogIdEnum.LIBRARY_CHANNEL],
        name: 'Library',
        type: 'channel',
        sortOrder: 'lastWatched',
        enabled: true,
        position: 3,
        showRemovedItems: false,
        isContinueWatching: false,
      },
      {
        id: CatalogIDStrings[CatalogIdEnum.LIBRARY_TV],
        name: 'Library',
        type: 'tv',
        sortOrder: 'lastWatched',
        enabled: true,
        position: 4,
        showRemovedItems: false,
        isContinueWatching: false,
      },
    ],
    cacheValiditySeconds: 3600, // 1 hour
    enableMetadataFetching: true,  // Default: enabled
    metadataProvider: 0,            // Default: Cinemeta (0)
    tmdbApiKey: undefined,          // No TMDB key by default
  };
}

/**
 * Encode configuration to Protobuf → gzip → base64 URL-safe
 */
export function encodeConfig(config: Config): string {
  debugConsole.log('Encoding configuration to Protobuf');

  try {
    // Convert to Protobuf message
    debugConsole.log(`Encoding auth_key: length=${config.authKey?.length || 0}`);
    debugConsole.log(`Encoding ${config.catalogs.length} catalogs (including ${config.catalogs.filter(c => c.isContinueWatching).length} Continue Watching)`);

    const protoMessage = {
      auth_key: config.authKey,
      group_by_type: config.groupByType,
      cache_validity_seconds: config.cacheValiditySeconds,
      addon_name: config.addonName || null,
      addon_description: config.addonDescription || null,
      catalogs: config.catalogs.map((cat) => ({
        id: cat.id,
        name: cat.name,
        type: cat.type,
        sort_order: cat.sortOrder,
        enabled: cat.enabled,
        position: cat.position,
        show_removed_items: cat.showRemovedItems || false,
        is_continue_watching: cat.isContinueWatching || false,  // Include the Continue Watching flag!
      })),
      rpdb_api_key: config.rpdbApiKey || null,
      enable_metadata_fetching: config.enableMetadataFetching !== undefined ? config.enableMetadataFetching : true,
      metadata_provider: config.metadataProvider !== undefined ? config.metadataProvider : 0,
      tmdb_api_key: config.tmdbApiKey || null,
    };

    // Serialize to Protobuf binary
    const message = librario.config.Config.create(protoMessage);
    const buffer = librario.config.Config.encode(message).finish();

    debugConsole.log(`Protobuf serialized: ${buffer.length} bytes`);

    // Compress with gzip
    const compressed = pako.gzip(buffer);
    debugConsole.log(`Gzip compressed: ${compressed.length} bytes`);

    // Encode to base64 URL-safe
    let base64 = btoa(String.fromCharCode.apply(null, Array.from(compressed)));

    // Make URL-safe by replacing +/ with -_ and removing =
    base64 = base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');

    debugConsole.log(`Base64 encoded: ${base64.length} chars`);

    return base64;
  } catch (error) {
    debugConsole.log(`Error encoding config: ${error}`);
    throw new Error(`Failed to encode configuration: ${error}`);
  }
}

/**
 * Decode configuration from base64 URL-safe → ungzip → Protobuf
 */
export function decodeConfig(encoded: string): Config {
  debugConsole.log('Decoding configuration from Protobuf');

  try {
    // Add back padding if needed
    let base64 = encoded;
    const missing = base64.length % 4;
    if (missing > 0) {
      base64 += '='.repeat(4 - missing);
    }

    // Convert URL-safe back to standard base64
    base64 = base64.replace(/-/g, '+').replace(/_/g, '/');

    // Decode from base64
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    debugConsole.log(`Base64 decoded: ${bytes.length} bytes`);

    // Decompress with gzip
    const decompressed = pako.ungzip(bytes);
    debugConsole.log(`Gzip decompressed: ${decompressed.length} bytes`);

    // Deserialize from Protobuf
    const message = librario.config.Config.decode(decompressed);

    // Build catalogs array from protobuf
    const catalogs: CatalogConfig[] = (message.catalogs || []).map((cat): CatalogConfig => ({
      id: cat.id || '',
      name: cat.name || '',
      type: (cat.type as any) || 'mixed',
      sortOrder: (cat.sort_order as any) || 'default',
      enabled: cat.enabled !== false,
      position: cat.position || 0,
      showRemovedItems: cat.show_removed_items || false,
      isContinueWatching: cat.is_continue_watching || false,  // Map the Continue Watching flag
    }));

    // Sort catalogs by position
    catalogs.sort((a, b) => a.position - b.position);

    // Convert to Config interface
    const config: Config = {
      authKey: message.auth_key || '',
      groupByType: message.group_by_type || false,
      cacheValiditySeconds: Number(message.cache_validity_seconds) || 3600,
      addonName: message.addon_name || undefined,
      addonDescription: message.addon_description || undefined,
      catalogs,
      rpdbApiKey: message.rpdb_api_key || undefined,
      enableMetadataFetching: message.enable_metadata_fetching !== undefined ? message.enable_metadata_fetching : true,
      metadataProvider: message.metadata_provider !== undefined ? message.metadata_provider : 0,
      tmdbApiKey: message.tmdb_api_key || undefined,
    };

    debugConsole.log('Configuration decoded successfully');

    return config;
  } catch (error) {
    debugConsole.log(`Error decoding config: ${error}`);
    throw new Error(`Failed to decode configuration: ${error}`);
  }
}

/**
 * Create shareable config (strips auth key, RPDB key, and TMDB key)
 */
export function createShareableConfig(config: Config): Config {
  debugConsole.log('Creating shareable configuration');

  return {
    ...config,
    authKey: '',
    rpdbApiKey: undefined,
    tmdbApiKey: undefined,
  };
}

